import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { RootDispatch, RootState } from "../../store";
import {
  fetchAdjustableStockThunk,
  adjustStockThunk,
} from "../../store/slices/warehouseSlice";
import {
  XCircle,
  Loader2,
  Save,
  Package,
  Warehouse,
  AlertTriangle,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  convertStockQuantity,
  getUnitsByType,
  getDefaultUnitForType,
} from "../../utils/warehouse.util";
import { fetchDropdownData } from "../../store/slices/storeInwardSlice";
import { fetchItemOptions } from "../../store/slices/quotationSlice";
import { IItemOption } from "../../models/quotationData.model";

type AdjustmentType = "increase" | "decrease";

interface AdjustStockModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdjustStockModal: React.FC<AdjustStockModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch: RootDispatch = useDispatch();

  const { warehouseNamesAndIds } = useSelector(
    (state: RootState) => state.warehouse,
  );

  const [selectedOption, setSelectedOption] = useState<IItemOption | null>(
    null,
  );
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("");
  const [adjustmentQty, setAdjustmentQty] = useState<string>("");
  const [adjustmentType, setAdjustmentType] =
    useState<AdjustmentType>("decrease");
  const [unit, setUnit] = useState<string>("");

  const [isFetchingStock, setIsFetchingStock] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStockData, setCurrentStockData] = useState<{
    qty: number;
    price: number;
    unit: string;
  } | null>(null);

  useEffect(() => {
    dispatch(fetchDropdownData());
  }, [dispatch]);

  const loadItemOptions: LoadOptions<
    IItemOption,
    any,
    { page: number }
  > = async (search, _loadedOptions, additional) => {
    const page = additional?.page ?? 1;
    try {
      const response = await dispatch(
        fetchItemOptions({ page, limit: 10, search }),
      ).unwrap();
      const options: IItemOption[] = response.data?.itemOptions || [];
      const totalPages = response.pagination?.totalPages ?? 1;

      return {
        options,
        hasMore: page < totalPages,
        additional: { page: page + 1 },
      };
    } catch {
      return { options: [], hasMore: false, additional: { page } };
    }
  };

  useEffect(() => {
    if (selectedOption?.type === "material") {
      setUnit(
        selectedOption.data.unitType
          ? getDefaultUnitForType(selectedOption.data.unitType) || ""
          : "",
      );
    } else {
      setUnit(selectedOption ? "Piece" : "");
    }
  }, [selectedOption]);

  useEffect(() => {
    const loadStock = async () => {
      if (selectedOption && selectedWarehouse) {
        setIsFetchingStock(true);
        try {
          const res = await dispatch(
            fetchAdjustableStockThunk({
              itemId: selectedOption.value,
              warehouseId: selectedWarehouse,
              type: selectedOption.type,
            }),
          ).unwrap();
          setCurrentStockData(res);
          if (res.unit) setUnit(res.unit);
        } catch (error: any) {
          toast.error(error);
          setCurrentStockData(null);
        } finally {
          setIsFetchingStock(false);
        }
      } else {
        setCurrentStockData(null);
      }
    };
    loadStock();
  }, [selectedOption, selectedWarehouse, dispatch]);

  const handleFinalSubmit = async () => {
    const quantity = Number(adjustmentQty);
    if (
      !selectedOption ||
      !selectedWarehouse ||
      !Number.isFinite(quantity) ||
      quantity <= 0 ||
      !unit
    ) {
      return toast.error("Please fill all fields correctly");
    }

    if (!currentStockData) {
      return toast.error("Unable to load current stock");
    }

    if (adjustmentType === "decrease" && currentStockData.qty <= 0) {
      return toast.error("No stock available to adjust");
    }

    if (adjustmentType === "decrease") {
      const availableStock =
        selectedOption.type === "material"
          ? convertStockQuantity(
              currentStockData.qty,
              currentStockData.unit,
              unit,
              selectedOption.data.unitType || "",
            )
          : currentStockData.qty;

      if (availableStock === null) {
        return toast.error("Unable to compare stock in the selected unit");
      }

      if (quantity > availableStock) {
        return toast.error(
          `Adjustment cannot exceed available stock (${availableStock})`,
        );
      }
    }

    setIsSubmitting(true);
    try {
      await dispatch(
        adjustStockThunk({
          itemId: selectedOption.value,
          type: selectedOption.type,
          warehouseId: selectedWarehouse,
          adjustmentQty: quantity,
          adjustmentType,
          unit,
        }),
      ).unwrap();

      toast.success("Stock adjustment recorded successfully");

      // ✅ RESET STATE AFTER SUCCESS
      setSelectedOption(null);
      setSelectedWarehouse("");
      setAdjustmentQty("");
      setAdjustmentType("decrease");
      setUnit("");
      setCurrentStockData(null);

      onClose();
    } catch (error: any) {
      toast.error(error || "Failed to adjust stock");
    } finally {
      setIsSubmitting(false);
    }
  };

  const customSelectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: "42px",
      fontSize: "0.875rem",
      borderColor: "#D1D5DB",
      borderRadius: "0.6rem",
      boxShadow: "none",
      "&:hover": { borderColor: "#0077C0" },
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  };

  if (!isOpen) return null;

  const isStockEmpty = !!currentStockData && currentStockData.qty <= 0;
  const isDecreaseUnavailable =
    adjustmentType === "decrease" && isStockEmpty;
  const stockQuantityInSelectedUnit =
    currentStockData && selectedOption?.type === "material"
      ? convertStockQuantity(
          currentStockData.qty,
          currentStockData.unit,
          unit,
          selectedOption.data.unitType || "",
        )
      : currentStockData?.qty ?? null;
  const stockDisplayUnit =
    selectedOption?.type === "material" && stockQuantityInSelectedUnit !== null
      ? unit
      : currentStockData?.unit;
  return (
    <div className="fixed inset-0 p-4 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[9999]">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-50 text-[#0077C0] rounded-lg">
              <Package size={20} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">Adjust Stock</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircle size={22} />
          </button>
        </div>

        <div className="p-6 space-y-5 text-sm">
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
              <Warehouse size={14} /> Select Warehouse
            </label>
            <select
              value={selectedWarehouse}
              onChange={(e) => setSelectedWarehouse(e.target.value)}
              className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#0077C0]"
            >
              <option value="">Choose a warehouse...</option>
              {warehouseNamesAndIds.map((w: any) => (
                <option key={w._id} value={w._id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
              Search Item
            </label>
            <AsyncPaginate
              value={selectedOption}
              onChange={setSelectedOption}
              loadOptions={loadItemOptions}
              additional={{ page: 1 }}
              formatOptionLabel={({ label, type }: IItemOption) => (
                <div className="flex justify-between items-center w-full text-sm">
                  <span>{label}</span>
                  <span
                    className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase shrink-0 ${type === "product" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}
                  >
                    {type}
                  </span>
                </div>
              )}
              styles={customSelectStyles}
              placeholder="Search product or material..."
              isClearable
              menuPortalTarget={document.body}
              debounceTimeout={300}
            />
          </div>

          {isFetchingStock ? (
            <div className="flex items-center justify-center py-4 gap-2 text-[#0077C0] text-sm italic">
              <Loader2 size={16} className="animate-spin" /> Fetching live stock
              data...
            </div>
          ) : (
            currentStockData && (
              <div
                className={`grid grid-cols-2 gap-4 p-4 rounded-xl border shadow-inner ${isStockEmpty ? "bg-red-50 border-red-100" : "bg-gray-50 border-gray-100"}`}
              >
                <div>
                  <p className="text-[10px] text-gray-400 uppercase font-bold">
                    Current Stock
                  </p>
                  <p
                    className={`text-lg font-bold ${isStockEmpty ? "text-red-600" : "text-gray-800"}`}
                  >
                    {stockQuantityInSelectedUnit ?? currentStockData.qty}{" "}
                    <span className="text-sm font-normal text-gray-500">
                      {stockDisplayUnit}
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase font-bold">
                    Total Value
                  </p>
                  <p className="text-lg font-bold text-gray-800">
                    ₹{currentStockData.price.toLocaleString()}
                  </p>
                </div>
                {isDecreaseUnavailable && (
                  <div className="col-span-2 flex items-center gap-2 text-red-500 text-[11px] font-bold mt-1">
                    <AlertTriangle size={14} /> Stock unavailable for adjustment
                  </div>
                )}
              </div>
            )
          )}

          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="col-span-2 space-y-1.5">
              <label className="text-[10px] font-bold text-gray-400 uppercase">
                Adjustment Direction
              </label>
              <div className="flex">
                <button
                  type="button"
                  aria-pressed={adjustmentType === "increase"}
                  onClick={() => setAdjustmentType("increase")}
                  className={`flex-1 px-3 py-2 border rounded-l-lg font-semibold ${
                    adjustmentType === "increase"
                      ? "border-green-500 bg-green-50 text-green-700"
                      : "border-gray-300 bg-white text-gray-600"
                  }`}
                >
                  Increase
                </button>
                <button
                  type="button"
                  aria-pressed={adjustmentType === "decrease"}
                  onClick={() => setAdjustmentType("decrease")}
                  className={`flex-1 px-3 py-2 border border-l-0 rounded-r-lg font-semibold ${
                    adjustmentType === "decrease"
                      ? "border-red-500 bg-red-50 text-red-700"
                      : "border-gray-300 bg-white text-gray-600"
                  }`}
                >
                  Decrease
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-gray-400 uppercase">
                Qty to {adjustmentType === "increase" ? "Increase" : "Decrease"}
              </label>
              <input
                type="number"
                min="0"
                step="any"
                value={adjustmentQty}
                disabled={isDecreaseUnavailable}
                onChange={(e) => setAdjustmentQty(e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#0077C0] text-sm font-semibold disabled:bg-gray-50 disabled:cursor-not-allowed"
                placeholder="0.00"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-gray-400 uppercase">
                Unit
              </label>
              {selectedOption?.type === "material" &&
              selectedOption.data.unitType ? (
                <select
                  value={unit}
                  disabled={isDecreaseUnavailable}
                  onChange={(e) => setUnit(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#0077C0] disabled:bg-gray-50"
                >
                  <option value="">Select Unit</option>
                  {getUnitsByType(selectedOption.data.unitType).map((u) => (
                    <option key={u.value} value={u.value}>
                      {u.label}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="p-2.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-500 text-center font-bold h-[42px] flex items-center justify-center">
                  Piece
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 bg-gray-50 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-white font-bold text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleFinalSubmit}
            disabled={
              !!(
                isSubmitting ||
                !selectedOption ||
                !selectedWarehouse ||
                !Number.isFinite(Number(adjustmentQty)) ||
                Number(adjustmentQty) <= 0 ||
                isDecreaseUnavailable
              )
            }
            className="flex-[2] py-2.5 bg-[#0077C0] text-white rounded-xl hover:bg-[#005B90] font-bold text-sm flex items-center justify-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed shadow-md"
          >
            {isSubmitting ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Save size={18} />
            )}
            {adjustmentType === "increase"
              ? "Confirm Increase"
              : "Record Shrink Loss"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdjustStockModal;
